/**
 * Pure helpers for chat reply reactions (Wave O.1).
 *
 * Mirrors iOS `LocalDataStore.toggleChatReaction` and Android
 * `ZoeyRepository.toggleChatReaction`. The shape of `reactions` is
 * `Record<emojiKey, userId[]>` — keys identify the emoji (e.g. "love",
 * "haha"), values list every `member_profiles/{uid}` that reacted.
 *
 * These functions are intentionally DOM-free and Firestore-free so the
 * caller can compute the next map locally and persist it via
 * `updateDoc(...)` (or any other transport). Pure inputs, pure outputs —
 * trivial to unit-test.
 */

export type ReactionMap = Record<string, string[]>;

/**
 * Toggle the current user's reaction with `emoji` on a `reactions` map.
 *
 * Cases handled:
 * - emoji has no entry yet → create entry with `[userId]`.
 * - entry exists, user not in list → append user.
 * - entry exists, user already in list → remove user; if the entry
 *   becomes empty, delete the emoji key entirely (matches iOS).
 *
 * Always returns a new object — never mutates the input.
 */
export function toggleReaction(
  reactions: ReactionMap | undefined,
  emoji: string,
  userId: string,
): ReactionMap {
  const next: ReactionMap = {};
  // Shallow copy of all existing emoji entries (excluding the one we're
  // toggling — that one is rebuilt below).
  if (reactions) {
    for (const key of Object.keys(reactions)) {
      if (key === emoji) continue;
      next[key] = [...reactions[key]];
    }
  }

  const current = reactions?.[emoji] ?? [];
  if (current.includes(userId)) {
    const filtered = current.filter((id) => id !== userId);
    if (filtered.length > 0) {
      next[emoji] = filtered;
    }
    // else: omit the key entirely (last user removed)
  } else {
    next[emoji] = [...current, userId];
  }

  return next;
}

/**
 * Return the list of emoji keys the given user has reacted with.
 * Order matches the iteration order of the `reactions` map.
 */
export function myReactions(
  reactions: ReactionMap | undefined,
  userId: string,
): string[] {
  if (!reactions || !userId) return [];
  return Object.keys(reactions).filter((key) =>
    reactions[key]?.includes(userId),
  );
}
